import {z} from 'zod';

import {
  CompleteFinancialProductPreferenceCoordinatesSchema as FinancialProductPreferenceCoordinatesSchema,
  FinancialProductsResponseBodyBaseSchema,
  ResolvedFinancialProductOfferSchema as FinancialProductOfferSchema,
  UpdateFinancialProductPreferenceRequestBodySchema,
  createFinancialProductPreferenceConflictResponseBodySchema,
  createFinancialProductPreferenceRefusalSchema,
} from 'features/financial-products';

export {
  FinancialProductTypeSchema,
  LeasingTargetGroupSchema,
  FinancialProductUnavailableReasonSchema,
  FinancialProductCalculationSchema,
  FinancialProductSelectionSchema,
  LeasingCellSchema,
  FinancialProductPreferenceRefusalReasonSchema,
} from 'features/financial-products';
export type {
  FinancialProductType,
  LeasingTargetGroup,
  FinancialProductUnavailableReason,
  FinancialProductMoney,
  FinancialProductCalculation,
  FinancialProductSelection,
  LeasingCell,
  FinancialProductPreferenceRefusalReason,
} from 'features/financial-products';
export {
  FinancialProductOfferSchema,
  FinancialProductPreferenceCoordinatesSchema,
  UpdateFinancialProductPreferenceRequestBodySchema,
};
export type FinancialProductOffer = z.infer<typeof FinancialProductOfferSchema>;
export type FinancialProductPreferenceCoordinates = z.infer<
  typeof FinancialProductPreferenceCoordinatesSchema
>;
export type UpdateFinancialProductPreferenceRequestBody = z.infer<
  typeof UpdateFinancialProductPreferenceRequestBodySchema
>;

/** A removed seller remains source `seller` with a null identity. */
export const FinancialProductPreferenceUpdatedBySchema = z.discriminatedUnion('source', [
  z.object({source: z.literal('customer'), seller: z.null()}),
  z.object({
    source: z.literal('seller'),
    seller: z.object({id: z.string(), displayName: z.string()}).nullable(),
  }),
]);
export type FinancialProductPreferenceUpdatedBy = z.infer<
  typeof FinancialProductPreferenceUpdatedBySchema
>;

export const FinancialProductsResponseBodySchema = FinancialProductsResponseBodyBaseSchema.extend({
  snapshotCapturedAt: z.string().datetime({offset: true}).nullable(),
  preferenceUpdatedAt: z.string().datetime({offset: true}).nullable(),
  preferenceUpdatedBy: FinancialProductPreferenceUpdatedBySchema.nullable(),
  standard: FinancialProductOfferSchema.nullable(),
  balloon: FinancialProductOfferSchema.nullable(),
  leasing: FinancialProductOfferSchema.nullable(),
});
export type FinancialProductsResponseBody = z.infer<typeof FinancialProductsResponseBodySchema>;

export const GetBusinessCaseFinancialProductsApiArgSchema = z.object({
  businessCaseId: z.string(),
});
export type GetBusinessCaseFinancialProductsApiArg = z.infer<
  typeof GetBusinessCaseFinancialProductsApiArgSchema
>;
export type GetBusinessCaseFinancialProductsApiResponse = FinancialProductsResponseBody;

export const UpdateBusinessCaseFinancialProductPreferenceApiArgSchema = z.object({
  businessCaseId: z.string(),
  requestBody: UpdateFinancialProductPreferenceRequestBodySchema,
});
export type UpdateBusinessCaseFinancialProductPreferenceApiArg = z.infer<
  typeof UpdateBusinessCaseFinancialProductPreferenceApiArgSchema
>;

/** Conflicts retain the authenticated response, including seller attribution. */
export const FinancialProductPreferenceConflictResponseBodySchema =
  createFinancialProductPreferenceConflictResponseBodySchema(FinancialProductsResponseBodySchema);
export const FinancialProductPreferenceRefusalSchema =
  createFinancialProductPreferenceRefusalSchema(
    FinancialProductPreferenceConflictResponseBodySchema
  );
export type FinancialProductPreferenceRefusal = z.infer<
  typeof FinancialProductPreferenceRefusalSchema
>;
